// Adverbs — how to form them (-mente), where they go, and the high-frequency
// ones worth memorising whole. A grouped reference table sits at the bottom.
import { renderGuideSections } from "../ui/guideSections.js";

const COMMON = {
  Time: [
    ["ahora", "now"], ["hoy", "today"], ["ayer", "yesterday"], ["mañana", "tomorrow"],
    ["siempre", "always"], ["nunca", "never"], ["ya", "already / now"], ["todavía", "still / yet"],
    ["temprano", "early"], ["tarde", "late"], ["luego", "later / then"], ["pronto", "soon"],
  ],
  Place: [
    ["aquí", "here"], ["ahí", "there"], ["allí", "over there"], ["cerca", "near"],
    ["lejos", "far"], ["dentro", "inside"], ["fuera", "outside"], ["arriba", "up / above"],
    ["abajo", "down / below"], ["delante", "in front"], ["detrás", "behind"], ["alrededor", "around"],
  ],
  Manner: [
    ["bien", "well"], ["mal", "badly"], ["así", "like this / so"], ["despacio", "slowly"],
    ["rápido", "fast"], ["juntos", "together"], ["claro", "clearly / of course"], ["mejor", "better"],
  ],
  Quantity: [
    ["muy", "very"], ["mucho", "a lot"], ["poco", "little"], ["bastante", "quite / enough"],
    ["demasiado", "too much"], ["más", "more"], ["menos", "less"], ["casi", "almost"],
  ],
};

export function adverbsView(app) {
  const block = (title, rows) => `
    <div class="bg-white rounded-2xl border border-sage-100 p-5">
      <h3 class="text-lg font-bold text-sage-800 mb-3">${title}</h3>
      <div class="overflow-x-auto"><table class="w-full text-sm">
        <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
          <th class="py-2 pr-3">Spanish</th><th class="py-2 px-3">English</th>
        </tr></thead>
        <tbody>
          ${rows
            .map(
              ([es, en]) => `<tr class="border-b border-sage-50 last:border-0">
            <td class="py-2 pr-3 font-medium text-sage-800">${es}</td>
            <td class="py-2 px-3 text-sage-700">${en}</td>
          </tr>`
            )
            .join("")}
        </tbody>
      </table></div>
    </div>`;

  const table = `
    <div class="grid sm:grid-cols-2 gap-4 mt-6">
      ${block("Time", COMMON.Time)}
      ${block("Place", COMMON.Place)}
      ${block("Manner", COMMON.Manner)}
      ${block("Quantity", COMMON.Quantity)}
    </div>`;

  renderGuideSections(
    app,
    "Adverbs",
    "Adverbs describe how, when, where, or to what degree something happens. Unlike adjectives, they never change form to agree with anything — one shape fits all.",
    [
      {
        title: "1. Forming adverbs with -mente",
        body: `
          <p class="text-sage-700 mb-3">Take the <b>feminine singular</b> of an adjective and add <b>-mente</b> (the equivalent of English <i>-ly</i>).</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5 mb-3">
            <li><i>rápida → <b>rápidamente</b></i> (quickly)</li>
            <li><i>lenta → <b>lentamente</b></i> (slowly)</li>
            <li><i>fácil → <b>fácilmente</b></i> (easily — no -a to change)</li>
          </ul>
          <p class="text-sage-700 text-sm"><b>Two in a row?</b> Only the last keeps -mente: <i>habla clara y lentamente</i>. The base adjective keeps any written accent: <i>rápida → rápidamente</i>.</p>
        `,
      },
      {
        title: "2. Position",
        body: `
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li>Adverbs of <b>manner</b> usually follow the verb: <i>Conduce <b>despacio</b>.</i></li>
            <li>Adverbs modifying an <b>adjective/adverb</b> go before it: <i><b>muy</b> alto, <b>bastante</b> bien</i>.</li>
            <li><i>Muy</i> vs <i>mucho</i>: <i>muy</i> modifies adjectives/adverbs (<i>muy rápido</i>); <i>mucho</i> modifies verbs/nouns (<i>come mucho</i>).</li>
          </ul>
        `,
      },
      {
        title: "3. Adverbial phrases (no -mente needed)",
        body: `
          <p class="text-sage-700 mb-2">Spanish often prefers a short phrase over a long -mente word:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>con cuidado</i> = carefully · <i>con frecuencia</i> = frequently</li>
            <li><i>de repente</i> = suddenly · <i>por fin</i> = finally · <i>a menudo</i> = often</li>
            <li><i>de nuevo</i> = again · <i>en seguida</i> = right away</li>
          </ul>
        `,
      },
    ],
    "",
    table
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
